import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { useDialog, useOnboarding } from "../DialogManager.tsx";
import {
  SampleExerciseLibrary,
  SampleRoutineCard,
  SampleWorkoutCard,
} from "../Samples.tsx";

export enum OnboardingDialogStep {
  Welcome = 0,
  ExerciseLibrary = 1,
  WorkoutRoutines = 2,
  CreateWorkoutFromRoutine = 3,
  Workouts = 4,
  Activity = 5,
  CircleMenu = 6,
  Done = 7,
}

interface OnboardingDialogProps {
  open: boolean;
}

const OnboardingDialog: React.FC<OnboardingDialogProps> = ({ open }) => {
  const { step, setStep } = useOnboarding();
  const { closeDialog } = useDialog();

  const handleClose = () => {
    localStorage.setItem("onboarding-completed", "true");
    closeDialog();
  };

  const handleNext = () => {
    if (step === OnboardingDialogStep.Done) {
      handleClose();
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => {
      return prevStep > 0 ? prevStep - 1 : prevStep;
    });
  };

  const title =
    step === OnboardingDialogStep.Welcome
      ? "Welcome to seriv.io!"
      : step === OnboardingDialogStep.ExerciseLibrary
        ? "Save Your Exercises"
        : step === OnboardingDialogStep.WorkoutRoutines
          ? "Design Workout Routines"
          : step === OnboardingDialogStep.CreateWorkoutFromRoutine
            ? "Design Workout Routines"
            : step === OnboardingDialogStep.Workouts
              ? "Track Your Workouts"
              : step === OnboardingDialogStep.Activity
                ? "View Your Friends' Activity"
                : step === OnboardingDialogStep.CircleMenu
                  ? "The Circle Menu"
                  : "You're All Set";
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title}
      closable={false}
      className={
        step === OnboardingDialogStep.ExerciseLibrary ||
        step === OnboardingDialogStep.WorkoutRoutines ||
        step === OnboardingDialogStep.CreateWorkoutFromRoutine ||
        step === OnboardingDialogStep.Workouts
          ? "pt-48 md:pt-0"
          : step === OnboardingDialogStep.CircleMenu
            ? "pb-48 md:pb-0"
            : ""
      }
    >
      <Dialog.Content>
        {step === OnboardingDialogStep.Welcome ? (
          <>
            <span>Let's walk through what seriv.io has to offer.</span>
          </>
        ) : step === OnboardingDialogStep.ExerciseLibrary ? (
          <>
            <span>
              Exercises form the foundation of your workouts. This is where you
              will find them. To make things easier for you, we've pre-populated
              your exercise library with some common exercises to get you
              started.
            </span>
            <div className="flex flex-col gap-2">
              <span className="text-gray-500 italic">
                Below is an example exercise library:
              </span>
              <SampleExerciseLibrary />
            </div>
          </>
        ) : step === OnboardingDialogStep.WorkoutRoutines ? (
          <>
            <span>
              If you like to repeat a workout, you can create a workout routine
              for it.
            </span>
            <div className="flex flex-col gap-2">
              <span className="text-gray-500 italic">
                Below is an example workout routine:
              </span>
              <SampleRoutineCard />
            </div>
          </>
        ) : step === OnboardingDialogStep.CreateWorkoutFromRoutine ? (
          <>
            <span>
              With a workout routine saved, a workout can be recorded in two
              clicks.
            </span>
            <div className="flex flex-col gap-2">
              <span className="text-gray-500 italic">
                Below is an example workout routine:
              </span>
              <SampleRoutineCard focusOnMenu={true} />
            </div>
          </>
        ) : step === OnboardingDialogStep.Workouts ? (
          <>
            <span>All of your recorded workouts will appear here.</span>
            <div className="flex flex-col gap-2">
              <span className="text-gray-500 italic">
                Below is an example workout:
              </span>
              <SampleWorkoutCard />
            </div>
          </>
        ) : step === OnboardingDialogStep.Activity ? (
          <>
            <span>
              View your friends' completed workouts in the activity feed.
            </span>
          </>
        ) : step === OnboardingDialogStep.CircleMenu ? (
          <span>
            Click on the circle menu to quickly record a workout, create an
            exercise, or a routine from anywhere in the app.
          </span>
        ) : step === OnboardingDialogStep.Done ? (
          <span>You're now ready to start using seriv.io!</span>
        ) : null}
      </Dialog.Content>
      <Dialog.Actions>
        {step > OnboardingDialogStep.Welcome && (
          <Button variant="neutral" onClick={handleBack}>
            Back
          </Button>
        )}
        <Button
          variant={step === OnboardingDialogStep.Done ? "primary" : "submit"}
          onClick={handleNext}
        >
          {step === OnboardingDialogStep.Done ? "Get Started" : "Next"}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { OnboardingDialog };
